import _ from 'lodash';
import money from '../../utils/money';

const MONTHS_IN_YEAR = 12;

const roundToAgorot = amount => _.round(amount, 2);

// A benefit created before annual pricing benefits existed carries no plan of its own, so its
// duration is the only signal: 12 months or more is a yearly benefit, anything shorter is monthly.
const isYearlyDuration = benefitAmount => benefitAmount >= MONTHS_IN_YEAR;

// A benefit that states its plan is taken at its word: benefitAmount then counts billing periods,
// so an annual benefit of 1 means one year and the duration rule would read it as monthly.
// A fixed price is per billing period, so duration says nothing about the plan — a fixed-price
// benefit that states no plan is monthly, never a yearly price to divide by 12.
const isYearlyPlan = ({ subscriptionType, benefitAmount, benefitPrice }) => (
  subscriptionType ? subscriptionType === 'YEARLY' : (!benefitPrice && isYearlyDuration(benefitAmount))
);

const applyDiscount = (price, benefitPercentage) => (
  price && benefitPercentage
    ? roundToAgorot(money.calculatePriceAfterDiscount(price, benefitPercentage))
    : null
);

// What the benefit takes off in total: the per-period saving across every period it covers.
const totalDiscount = ({ fullPrice, price, periods }) => (
  fullPrice && price ? roundToAgorot((fullPrice - price) * (periods || 1)) : null
);

// Each plan is discounted off its own base — the monthly and annual prices are independent backend
// values, and the annual one is already cheaper per month than paying monthly.
// A fixed-price benefit states the price outright, so there is nothing to discount.
function planPricing({ monthlyPrice, annualPrice, benefitPercentage, benefitAmount, benefitPrice, subscriptionType }) {
  const isYearly = isYearlyPlan({ subscriptionType, benefitAmount, benefitPrice });
  const fullPrice = isYearly ? annualPrice : monthlyPrice;
  const price = benefitPrice || applyDiscount(fullPrice, benefitPercentage);
  if (!price) {
    return null;
  }
  return {
    price,
    fullPrice,
    monthlyPrice: isYearly ? roundToAgorot(price / MONTHS_IN_YEAR) : price,
  };
}

export default {
  MONTHS_IN_YEAR,
  roundToAgorot,
  isYearlyDuration,
  isYearlyPlan,
  applyDiscount,
  totalDiscount,
  planPricing,
};
